import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X, Check, Loader2 } from 'lucide-react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useSharedCart } from '@/components/shared-cart/SharedCartProvider';
import { useUserStore } from '@/stores/userStore';
import { API_BASE_URL, authHeaders } from '@/services/config';

interface Share {
  id: string;
  userId: string;
  displayName: string;
  amountCents: number;
  currency: string;
  status: 'pending' | 'paid';
  clientSecret?: string;
}

interface CheckoutState {
  shares: Share[];
  stripeConfigured: boolean;
  publishableKey: string;
}

interface OrderLineItem {
  id: string;
  title: string;
  quantity: number;
  unitPriceCents: number;
  totalCents: number;
  addedByName: string;
}

interface OrderPayment {
  id: string;
  userId: string;
  displayName: string;
  amountCents: number;
}

interface Order {
  orderNumber: string;
  sessionName: string;
  subtotalCents: number;
  currency: string;
  placedAt: string;
  lineItems: OrderLineItem[];
  payments: OrderPayment[];
}

function money(cents: number, currency: string): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency.toUpperCase() }).format(
    cents / 100
  );
}

// Carries the server's reason code alongside the message, so the modal can
// act on "we need an address" rather than only reporting it.
class CheckoutError extends Error {
  code?: string;
  constructor(message: string, code?: string) {
    super(message);
    this.code = code;
  }
}

async function checkoutApi(cartId: string, path: string, method: 'GET' | 'POST' = 'GET') {
  const res = await fetch(`${API_BASE_URL}/shared-carts/${cartId}/checkout${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new CheckoutError(body.error || `HTTP ${res.status}`, body.code);
  return body.data;
}

interface Address {
  name: string;
  line1: string;
  line2: string;
  city: string;
  province: string;
  country: string;
  postcode: string;
  phone: string;
}

const emptyAddress: Address = {
  name: '', line1: '', line2: '', city: '', province: '', country: '', postcode: '', phone: '',
};

async function saveAddress(cartId: string, address: Address) {
  const res = await fetch(`${API_BASE_URL}/shared-carts/${cartId}/delivery-address`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(address),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.details || body.error || `HTTP ${res.status}`);
  return body.data?.address as Address;
}

// The receipt exists only once the last share lands, so a 404 here is the
// normal state for a checkout still in progress.
async function fetchOrder(cartId: string): Promise<Order | null> {
  const res = await fetch(`${API_BASE_URL}/shared-carts/${cartId}/order`, {
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
  });
  if (!res.ok) return null;
  const body = await res.json().catch(() => ({}));
  return body?.data?.order ?? null;
}

// Everyone pays their share; the order completes when the last share lands.
function StripePayForm({ cartId, onPaid }: { cartId: string; onPaid: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pay = async () => {
    if (!stripe || !elements) return;
    setPaying(true);
    setError(null);
    try {
      const result = await stripe.confirmPayment({ elements, redirect: 'if_required' });
      if (result.error) {
        setError(result.error.message || 'Payment failed');
        return;
      }
      await checkoutApi(cartId, '/confirm', 'POST');
      onPaid();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Payment failed');
    } finally {
      setPaying(false);
    }
  };

  return (
    <div className="space-y-4">
      <PaymentElement />
      {error && <p className="text-sm text-red-600">{error}</p>}
      <button onClick={pay} disabled={paying || !stripe} className="btn-primary w-full">
        {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        {paying ? 'Paying…' : 'Pay my share'}
      </button>
    </div>
  );
}

// Where the order goes. One address for the group, because they are buying
// one thing together — and everyone sees it before they pay.
function DeliveryAddressForm({
  cartId,
  onSaved,
}: {
  cartId: string;
  onSaved: () => void;
}) {
  const [address, setAddress] = useState<Address>(emptyAddress);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const field = (key: keyof Address) => ({
    value: address[key],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      setAddress(prev => ({ ...prev, [key]: e.target.value })),
    className: 'w-full rounded-lg border border-line px-3 py-2 focus:border-coral focus:outline-none',
  });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await saveAddress(cartId, address);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the address');
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <p className="text-sm text-ink/60">
        Where is this going? One address for the whole order — everyone can see it.
      </p>

      <input {...field('name')} placeholder="Full name" autoComplete="name" required />
      <input {...field('line1')} placeholder="Street address" autoComplete="address-line1" required />
      <input {...field('line2')} placeholder="Apartment, suite (optional)" autoComplete="address-line2" />
      <div className="grid grid-cols-2 gap-3">
        <input {...field('city')} placeholder="City" autoComplete="address-level2" required />
        <input {...field('province')} placeholder="Province / state" autoComplete="address-level1" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <input {...field('country')} placeholder="Country" autoComplete="country-name" required />
        <input {...field('postcode')} placeholder="Postcode" autoComplete="postal-code" />
      </div>
      <input {...field('phone')} placeholder="Phone (optional)" autoComplete="tel" />

      {error && <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}

      <button type="submit" disabled={saving} className="btn-primary w-full">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        {saving ? 'Saving…' : 'Use this address'}
      </button>
    </form>
  );
}

// What the party bought, who put each thing in the cart, and who paid for
// what — the part of the evening worth keeping.
function Receipt({ order, myId, onClose }: { order: Order; myId: string; onClose: () => void }) {
  // The store's total can exceed what the goods themselves cost — tax it
  // worked out from the delivery address, and anything else it added. Left
  // unlabelled the receipt simply fails to add up, which reads as a bug.
  const itemsTotal = order.lineItems.reduce((sum, item) => sum + item.totalCents, 0);
  const storeAdjustment = order.subtotalCents - itemsTotal;

  return (
    <div>
      <div className="mb-5 rounded-xl bg-aisle-soft p-4 text-center">
        <p className="font-display text-lg font-bold text-aisle">Order complete 🎉</p>
        <p className="font-mono text-sm text-ink/60">{order.orderNumber}</p>
        <p className="mt-1 text-xs text-ink/50">
          {new Date(order.placedAt).toLocaleString()}
        </p>
      </div>

      <ul className="mb-4 divide-y divide-line rounded-xl border border-line">
        {order.lineItems.map(item => (
          <li key={item.id} className="flex items-start justify-between gap-3 px-4 py-3">
            <div className="min-w-0">
              <p className="truncate font-medium">{item.title}</p>
              <p className="text-xs text-ink/50">
                {item.quantity} × {money(item.unitPriceCents, order.currency)}
                {item.addedByName ? ` · added by ${item.addedByName}` : ''}
              </p>
            </div>
            <span className="shrink-0 font-mono text-sm">{money(item.totalCents, order.currency)}</span>
          </li>
        ))}
        {storeAdjustment !== 0 && (
          <li className="flex items-center justify-between px-4 py-2.5 text-sm text-ink/60">
            <span>{storeAdjustment > 0 ? 'Tax and shipping' : 'Discount'}</span>
            <span className="font-mono">{money(storeAdjustment, order.currency)}</span>
          </li>
        )}
        <li className="flex items-center justify-between bg-paper px-4 py-3 font-semibold">
          <span>Total</span>
          <span className="font-mono">{money(order.subtotalCents, order.currency)}</span>
        </li>
      </ul>

      <p className="mb-2 text-xs uppercase tracking-wide text-ink/40">Who paid</p>
      <ul className="mb-5 divide-y divide-line rounded-xl border border-line">
        {order.payments.map(payment => (
          <li key={payment.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
            <span className={payment.userId === myId ? 'font-semibold' : ''}>
              {payment.userId === myId ? 'You' : payment.displayName}
            </span>
            <span className="flex items-center gap-2 font-mono">
              {money(payment.amountCents, order.currency)}
              <Check className="h-3.5 w-3.5 text-aisle" />
            </span>
          </li>
        ))}
      </ul>

      <button onClick={onClose} className="btn-primary w-full">
        Done
      </button>
    </div>
  );
}

export function CheckoutModal({ onClose }: { onClose: () => void }) {
  const { sharedCart, addMessageListener } = useSharedCart();
  const myId = useUserStore(s => s.user?.id) || '';
  const [state, setState] = useState<CheckoutState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [order, setOrder] = useState<Order | null>(null);
  // Set when the bill was torn up because the cart moved under it.
  const [recalculated, setRecalculated] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  // The store will not open a bill until it knows where the goods go.
  const [needsAddress, setNeedsAddress] = useState(false);
  // Cancelling voids the bill, and the void broadcast comes straight back
  // to this modal. Without this the refresh would helpfully open a brand
  // new checkout on the way out the door.
  const abandoned = useRef(false);

  const cartId = sharedCart?.id || '';

  const refresh = useCallback(async () => {
    if (!cartId || abandoned.current) return;
    try {
      const data = await checkoutApi(cartId, '', 'GET');
      if (!data.shares || data.shares.length === 0) {
        setState(await checkoutApi(cartId, '/start', 'POST'));
      } else {
        setState(data);
      }
    } catch (err) {
      // No shares yet → start checkout
      try {
        setState(await checkoutApi(cartId, '/start', 'POST'));
        setNeedsAddress(false);
      } catch (startErr) {
        if (startErr instanceof CheckoutError && startErr.code === 'delivery_address_required') {
          // Not a failure to report — a question to ask.
          setNeedsAddress(true);
          setError(null);
          return;
        }
        setError(startErr instanceof Error ? startErr.message : 'Failed to start checkout');
      }
    }
  }, [cartId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Live updates as friends pay their shares, and as the bill is redrawn
  // when someone changes the cart mid-checkout.
  useEffect(() => {
    const unsubscribe = addMessageListener(message => {
      if (message.type === 'checkout_voided') {
        setRecalculated(true);
        refresh();
        return;
      }
      if (['checkout_share_paid', 'checkout_completed', 'checkout_started'].includes(message.type)) {
        refresh();
      }
    });
    return unsubscribe;
  }, [addMessageListener, refresh]);

  useEffect(() => {
    if (state?.stripeConfigured && state.publishableKey && !stripePromise) {
      setStripePromise(loadStripe(state.publishableKey));
    }
  }, [state, stripePromise]);

  const myShare = useMemo(() => state?.shares.find(s => s.userId === myId), [state, myId]);
  const total = useMemo(() => state?.shares.reduce((sum, s) => sum + s.amountCents, 0) || 0, [state]);
  const allPaid = !!state && state.shares.length > 0 && state.shares.every(s => s.status === 'paid');
  const currency = state?.shares[0]?.currency || 'usd';

  // Pull the receipt once the party is fully paid up.
  useEffect(() => {
    if (!allPaid || !cartId || order) return;
    let cancelled = false;
    fetchOrder(cartId).then(fetched => {
      if (!cancelled && fetched) setOrder(fetched);
    });
    return () => {
      cancelled = true;
    };
  }, [allPaid, cartId, order]);

  // Back out while the bill is still only a proposal, releasing any card
  // authorisations the party is holding.
  const cancelCheckout = async () => {
    setCancelling(true);
    abandoned.current = true;
    try {
      await checkoutApi(cartId, '/cancel', 'POST');
      onClose();
    } catch (err) {
      abandoned.current = false;
      setError(err instanceof Error ? err.message : 'Could not cancel the checkout');
      setCancelling(false);
    }
  };

  const paySimulated = async () => {
    setConfirming(true);
    setError(null);
    try {
      await checkoutApi(cartId, '/confirm', 'POST');
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Payment failed');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/70 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-lift">
        <div className="mb-5 flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold">Check out together</h2>
            <p className="text-sm text-ink/60">The bill is split evenly. Everyone pays their own share.</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 hover:bg-paper" aria-label="Close checkout">
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && <p className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}

        {recalculated && !order && (
          <p className="mb-4 rounded-lg bg-coral-soft p-3 text-sm text-coral-deep">
            The cart changed, so the split was recalculated. Check the new amounts before paying.
          </p>
        )}

        {needsAddress && !state ? (
          <DeliveryAddressForm
            cartId={cartId}
            onSaved={() => {
              setNeedsAddress(false);
              refresh();
            }}
          />
        ) : !state ? (
          <div className="flex items-center justify-center py-10 text-ink/50">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Preparing checkout…
          </div>
        ) : order ? (
          <Receipt order={order} myId={myId} onClose={onClose} />
        ) : (
          <>
            {/* Shares */}
            <ul className="mb-5 divide-y divide-line rounded-xl border border-line">
              {state.shares.map(share => (
                <li key={share.id} className="flex items-center justify-between px-4 py-3">
                  <span className={share.userId === myId ? 'font-semibold' : ''}>
                    {share.userId === myId ? 'You' : share.displayName}
                  </span>
                  <span className="flex items-center gap-2 font-mono text-sm">
                    {money(share.amountCents, share.currency)}
                    {share.status === 'paid' ? (
                      <span className="flex items-center gap-1 rounded-full bg-aisle-soft px-2 py-0.5 text-xs font-medium text-aisle">
                        <Check className="h-3 w-3" /> Paid
                      </span>
                    ) : (
                      <span className="rounded-full bg-paper px-2 py-0.5 text-xs text-ink/50">Waiting</span>
                    )}
                  </span>
                </li>
              ))}
              <li className="flex items-center justify-between bg-paper px-4 py-3 font-semibold">
                <span>Total</span>
                <span className="font-mono">{money(total, currency)}</span>
              </li>
            </ul>

            {allPaid ? (
              <div className="rounded-xl bg-aisle-soft p-4 text-center">
                <p className="font-display text-lg font-bold text-aisle">Order complete 🎉</p>
                <p className="text-sm text-ink/60">Everyone's share is in. Nice shopping, team.</p>
              </div>
            ) : myShare?.status === 'paid' ? (
              <p className="text-center text-sm text-ink/60">
                Your share is paid. Waiting for the rest of the party…
              </p>
            ) : state.stripeConfigured && myShare?.clientSecret && stripePromise ? (
              <Elements
                key={myShare.clientSecret}
                stripe={stripePromise}
                options={{ clientSecret: myShare.clientSecret }}
              >
                <StripePayForm cartId={cartId} onPaid={refresh} />
              </Elements>
            ) : myShare ? (
              <div className="space-y-2">
                <button onClick={paySimulated} disabled={confirming} className="btn-primary w-full">
                  {confirming ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Pay my share · {money(myShare.amountCents, myShare.currency)}
                </button>
                {!state.stripeConfigured && (
                  <p className="text-center text-xs text-ink/40">
                    Simulated payment — connect Stripe to take real payments.
                  </p>
                )}
              </div>
            ) : (
              <p className="text-center text-sm text-ink/60">
                You don't have a share in this checkout (it was split before you joined).
              </p>
            )}

            {!allPaid && !state.shares.some(s => s.status === 'paid') && (
              <button
                onClick={cancelCheckout}
                disabled={cancelling}
                className="mt-3 w-full rounded-lg py-2 text-sm text-ink/50 hover:bg-paper hover:text-ink"
              >
                {cancelling ? 'Cancelling…' : 'Cancel and keep shopping'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
